import { useState } from "react";
import { ConversationalModule } from "@gen/agency/v1/agency_pb";
import { DAP } from "@gen/dap/v1/dap_pb";
import { getDAP } from "@services/dap";
import { ConnectError, Code } from "@connectrpc/connect";
import { useToaster } from "@hooks/useToaster";
import { useTranslation } from "react-i18next";
import { useUserStore } from "@stores/userStore";
import { useChatStore } from "@stores/chatStore";
import { Role } from "@gen/user/v1/user_pb";

interface UseFetchDapOptions {
  showToastOnError?: boolean;
}

interface UseFetchDapResult {
  dap: DAP | undefined;
  isLoading: boolean;
  errorDapMessage: string | undefined;
  conversationalModule: ConversationalModule | undefined;
  fetchDap: (id: string | null, name: string | null) => Promise<void>;
}

export function useFetchDap(
  options: UseFetchDapOptions = {},
): UseFetchDapResult {
  const { user } = useUserStore();
  const { showToastOnError = true } = options;
  const [dap, setDap] = useState<DAP | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorDapMessage, setErrorDapMessage] = useState<string | undefined>();
  const { module: conversationalModule, setModule: setConversationalModule } =
    useChatStore();

  const { addToast } = useToaster();
  const { t } = useTranslation();

  const ASK_FOR_BENEFICIARY_NAME = user?.role === Role.USER;

  const fetchDap = async (id: string | null, name: string | null) => {
    if (!id) {
      setDap(undefined);
      setErrorDapMessage(undefined);
      return;
    }

    if (ASK_FOR_BENEFICIARY_NAME && !name) {
      setDap(undefined);
      setErrorDapMessage(t("booking.unknownNumber"));
      return;
    }

    setIsLoading(true);
    setErrorDapMessage(undefined);

    try {
      const response = await getDAP(id, name || "");
      if (response.error) {
        setErrorDapMessage(response.error);
      }
      setDap(response.dap);
      setConversationalModule(response.conversationalModule);
    } catch (e: unknown) {
      if (e instanceof ConnectError) {
        if (e.code === Code.NotFound) {
          setErrorDapMessage(e.rawMessage || t("booking.unknownNumber"));
        } else if (showToastOnError) {
          addToast("error", e.rawMessage || t("genericError"));
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { dap, isLoading, errorDapMessage, fetchDap, conversationalModule };
}
